import { module } from 'modujs';
import { gsap } from 'gsap';

const WORD_CLASS = 'word-wrap';
const LINE_CLASS = 'line-wrap';
const LINE_INNER_CLASS = 'line-inner';
const CHAR_CLASS = 'char-wrap';
const VISIBLE_CLASS = 'is-visible';
const IS_ITALIC = 'is-italic';
const IS_BOLD = 'is-bold';
const ANIMATED_CLASS = 'is-animated'

// data-split-type= 'lines, words or chars"
// data-split-animate= 'lines, words or chars"
// data-split-auto - animation launched automatically
// data-split-delay - update delay of the timeline
// data-split-reverse-delay - update delay of the timeline for the reverse animation
// data-split-stagger - update the stagger of the timeline
// data-split-from - update the "from" to the timeline
// data-split-to - update the "to" of the timeline

// CONDITION
// You can set a condition with this.condition, and set data-split-condition
// if condition is true, 
// - You can add data-split-from-condition, and data-split-to-condition

export default class extends module {
    constructor(m) {
        super(m);

        this.DEFAULT_FROM_ANIMATION = {
            y: '120%',
            ease: "power2.in"
        }
        
        this.DEFAULT_TO_ANIMATION = {
            y: 0,
            duration: 0.6,
            stagger: 0.03,
            paused: true,
            force3D: true,
            ease: "power2.out"
        }

    }

    init() {
        this.windowWidth = window.innerWidth;

        this.isReady = false;
        this.resized = false;

        this.condition = window.innerWidth < window.innerHeight;
        
        this.hasCondition = typeof this.getData('condition') == 'string';
        
        this.splitMode = this.getData('type') ? this.getData('type') : 'chars';
        this.isAnimated = typeof this.getData('animated') == 'string';
        this.animateMode = typeof this.getData('animate') == 'string' ? this.getData('animate') : this.isAnimated ? this.splitMode : false;
        
        this.isIndent = typeof this.getData('indent') == 'string';

        if(typeof this.getData('from') == 'string') {
            this.DEFAULT_FROM_ANIMATION = JSON.parse(this.getData('from'));
        }
        if(this.hasCondition && this.condition && typeof this.getData('from-condition') == 'string') {
            this.DEFAULT_FROM_ANIMATION = JSON.parse(this.getData('from-condition'));
        }

        if(typeof this.getData('to') == 'string') {
            this.DEFAULT_TO_ANIMATION = JSON.parse(this.getData('to'));
        }
        if(this.hasCondition && this.condition && typeof this.getData('to-condition') == 'string') {
            let to = JSON.parse(this.getData('to-condition'));
            this.DEFAULT_TO_ANIMATION = {...to,...this.DEFAULT_TO_ANIMATION};
        }

        if(typeof this.getData('stagger') == 'string') {
            this.DEFAULT_TO_ANIMATION.stagger = parseFloat(this.getData('stagger'));
        }
        if(typeof this.getData('duration') == 'string') {
            this.DEFAULT_TO_ANIMATION.duration = parseFloat(this.getData('duration'));
        }
        if(typeof this.getData('delay') == 'string') {
            this.DEFAULT_TO_ANIMATION.delay = parseFloat(this.getData('delay'));
        }
        if(typeof this.getData('reverse-delay') == 'string') {
            this.reverseDelay = parseFloat(this.getData('reverse-delay'));
        } else {
            this.reverseDelay = 0;
        }
        
        this.animated = false;
        
        this.baseText = this.el.innerHTML;

        // prevent specials chars &amp;
        this.baseText = this.baseText.replace('&amp;','&');

        gsap.delayedCall(window.readyDelay,() => {
            this.split();
        });

        gsap.delayedCall(window.readyCallbackDelay,() => {
            this.bindResize = this.resize.bind(this);
            window.addEventListener('resize', this.bindResize);
        });

    }

    split(resize = false) {
        this.splitChars(this.el);

        gsap.delayedCall(0.15,() => {
            if(this.splitMode == 'lines') {
                this.splitLines();
            }
            else if(this.splitMode == 'words') {
                this.splitWords();
            }
    
            this.animations = new Array();
            if(this.isAnimated) {
                this.initFromAnimation(this.animateMode);
            }

            let animationDelay = 0;
            if(!this.resized) {
                animationDelay = window.readyCallbackDelay - 0.6;
            } 

            gsap.delayedCall(animationDelay,() => {
                this.isReady = true;
                this.el.classList.add(VISIBLE_CLASS);
                this.launch();
            })
        });

    }

    launch() {
        if(typeof this.el.getAttribute('data-scroll') != 'string') {
            if((typeof this.getData('auto') == 'string' || this.resized) && this.isAnimated) {
                this.animate();
            }
        }
        
    }

    trigger(e) {
        
        if(e.mode == 'enter') {
            this.animate('scroll');
        }
        if(e.mode == 'leave') {
            this.animate('scroll', true);
        }

    }

    splitChars(el) {
        el.innerHTML = el.innerHTML.replace('<br>','');
        
        let chars = el.innerHTML.split('');

        el.innerHTML = '';

        let isItalic = false;

        for (let i = 0; i < chars.length; i++) {
            let span = document.createElement('span');
                        
            // Manage italic
            if(chars[i] == '[') {
                isItalic = true;
                chars.splice(i, 1);
            } 

            if(chars[i] == ']') {
                isItalic = false;
                chars.splice(i, 1);
            }

            // Prevent amp; after a `&`
            if(chars[i] == '&') {
                chars.splice(i+1, 4);
            }
            
            if(chars[i] == undefined) return;

            span.innerText = chars[i] == '' ? '\xa0\xa0' : chars[i];
            span.classList.add(CHAR_CLASS);


            if(isItalic) {
                span.classList.add(IS_ITALIC);
            }

            if(i == 0 && this.isIndent) {
                this.addIndentation(el);
            }
            
            el.appendChild(span);
            
        }

    }

    splitLines() {
        let $wordSpans = Array.from(this.el.querySelectorAll('span'));
        let lineArray = [],
            lineIndex = 0,
            lineStart = true;

        $wordSpans.forEach((el, idx) => {
            let top = el.getBoundingClientRect().top;

            if (lineStart) {
                lineArray[lineIndex] = [idx];
                lineStart = false;

            } else {
                let $next = el.nextSibling;

                if ($next != null) {
                    if ($next.getBoundingClientRect().top > top) {
                        lineArray[lineIndex].push(idx);
                        lineIndex++;
                        lineStart = true
                    }
                } else {
                    lineArray[lineIndex].push(idx);
                }
            }
        });

        for (let i = 0; i < lineArray.length; i++) {
            let start = lineArray[i][0];
            let end = lineArray[i][1] + 1;

            let wrap = document.createElement('div');
            wrap.classList.add(LINE_CLASS);
            
            let inner = document.createElement('span');
            inner.classList.add(LINE_INNER_CLASS);
            wrap.appendChild(inner);
            
            if(i == 0 && this.isIndent) {
                this.addIndentation(inner);
            }

            /* no end value pushed to array if only one word last line*/
            if (!end) {
                inner.innerText = `${inner.innerText}${$wordSpans[start].innerText}`
            } else {
                let slicedSpans = $wordSpans.slice(start, end);
                slicedSpans.forEach((el,index) => {

                    if(el.innerText == '' && index != slicedSpans.length-1) {
                        inner.innerHTML = `${inner.innerHTML} `;
                    } else {
                        // inner.innerText = `${inner.innerText}${el.innerText}`;
                        inner.append(el.cloneNode(true));
                    }
                    el.parentNode.removeChild(el);

                });
            }

            this.el.appendChild(wrap);
        }
    }

    splitWords() {
        let $wordSpans = Array.from(this.el.querySelectorAll('span'));
        let wordArray = [],
            wordIndex = 0,
            wordStart = true;

        $wordSpans.forEach((el, idx) => {
            let top = el.getBoundingClientRect().top;

            if (wordStart) {
                wordArray[wordIndex] = [idx];
                wordStart = false;

            } else {
                let $next = el.nextSibling;
                if ($next != null) {

                    if ($next.innerText == '' || $next.innerText == ' ') {
                        wordArray[wordIndex].push(idx);
                        wordIndex++;
                        wordStart = true;
                    }
                } else {
                    wordArray[wordIndex].push(idx);
                }
            }
        });

        for (let i = 0; i < wordArray.length; i++) {
            let start = wordArray[i][0];
            let end = wordArray[i][1] + 1;

            let wrap = document.createElement('span');
            wrap.classList.add(WORD_CLASS);

            /* no end value pushed to array if only one word last line*/
            if (!end) {
                wrap.appendChild($wordSpans[start]);
            } else {

                let slicedSpans = $wordSpans.slice(start, end);
                slicedSpans.forEach(el => {
                    wrap.appendChild(el);
                });
            }

            this.el.appendChild(wrap);
        }
    }

    addIndentation(wrap) {
        
        let indent = document.createElement('span');
        indent.classList.add('indent');
        wrap.appendChild(indent);
        
    }

    initFromAnimation(mode) {        
        let $wrap;

        if(this.splitMode == 'words') {
            $wrap = this.el.querySelectorAll(`.${WORD_CLASS}`);
        } else if(this.splitMode == 'lines') {
            $wrap = this.el.querySelectorAll(`.${LINE_CLASS}`);
        } else if(this.splitMode == 'chars') {
            $wrap = this.el;
        }

        if($wrap.length != undefined) {

            $wrap.forEach(($wrapItem) => {
                let animation;
                let $items;

                if(mode == 'chars' || mode == '') {
                    $items = $wrapItem.querySelectorAll(`.${CHAR_CLASS}`);
                } else if(mode =='words') {
                    $items = $wrap;
                } else if(mode == 'lines') {
                    $items = $wrapItem.querySelectorAll(`.${LINE_INNER_CLASS}`);
                } else {
                    console.warn(`Animation can't be created because there is a conflict between your split type and the animation type`)
                    return;
                }

                gsap.set($items, {
                    ...this.DEFAULT_FROM_ANIMATION
                });

                animation = gsap.to($items,{
                    ...this.DEFAULT_TO_ANIMATION
                });
                animation.pause();

                this.animations.push(animation);
            });
        } else {
            let $items = $wrap.querySelectorAll(`.${CHAR_CLASS}`);
            let animation;

            gsap.set($items, {
                ...this.DEFAULT_FROM_ANIMATION
            });
            
            animation = gsap.to($items,{
                ...this.DEFAULT_TO_ANIMATION
            });
            animation.pause();
            this.animations.push(animation);
        }
    }

    animate(mode = 'auto', reversed = false) {

        if(mode == 'scroll' && !this.isReady) {
            gsap.delayedCall(0.3, () => {
                this.animate('scroll');
            });
            return;
        }

        this.animations.forEach((animation, index) => {
            let delay = (reversed) ? this.reverseDelay : this.DEFAULT_TO_ANIMATION.delay
            
            if(this.delayedCall != undefined) {
                this.delayedCall.kill();
            }
            
            this.delayedCall = gsap.delayedCall(delay,() => {
                if(!reversed) {
                    // animation.progress(0);
                    animation.play();

                    if(index == 0) {
                        this.el.classList.add(ANIMATED_CLASS);
                    }
                } else {
                    // animation.progress(1);
                    animation.reverse();

                    if(index == 0) {
                        this.el.classList.remove(ANIMATED_CLASS);
                    }
                }    
            });
        });

        this.animated = true;

    }

    resize() {

        if(this.resizeTimeout != undefined) {
            this.resizeTimeout.kill();
        }
        this.resizeTimeout = gsap.delayedCall(0.2, () => {
            this.resizeDebounce();
        });
    }

    resizeDebounce() {
        if(this.windowWidth != window.innerWidth || !window.isMobile) {
            if (this.splitTimeout != undefined) {
                this.splitTimeout.kill();
            }
            this.isReady = false;
            this.resized = true;

            this.el.classList.remove(VISIBLE_CLASS);
            this.el.innerText = this.baseText;

            this.windowWidth = window.innerWidth;

            this.splitTimeout = gsap.delayedCall(0.2, () => {
                this.split();
            });
        }
    }

    destroy() {
        window.removeEventListener('resize', this.bindResize)
    }

}
